import algoliasearch from 'algoliasearch';
import uniq from 'lodash/uniq';
import Logger from '../../logger';

const getFilterGroupNames = (algoliaConfig, indexName) => {
  const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey, { protocol: 'https:' });
  const index = client.initIndex(indexName);

  return new Promise((resolve, reject) => index.search('', {
    hitsPerPage: 0,
    facets: '*',
    highlightPreTag: '',
    highlightPostTag: ''
  }, (err, result) => {
    if (err) {
      Logger.logWarn('Failed to get FilterGroupNames from algolia', err.stack)
      reject(err);
    }

    resolve(Object.keys(result.facets));
  }));
};

const getFilterData = (algoliaConfig, indexName, filterParameter='') => {
  const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey, { protocol: 'https:' });
  const index = client.initIndex(indexName);

  return new Promise((resolve, reject) => index.search('', {
    hitsPerPage: 0,
    facets: '*',
    filters: filterParameter,
    highlightPreTag: '',
    highlightPostTag: ''
  }, (err, result) => {
    if (err) {
      reject(err);
    }

    resolve({ facets: result?.facets ? Object.assign(result.facets) : {} });
  }));
};

const extractLeafNode = (answers, leafNodesArray) => {
  let currentLeaf = leafNodesArray;
  answers.forEach((answer) => {
    if (answer.answers === undefined || answer.answers.length === 0) {
      currentLeaf.push(answer.queryText.split(':')[0]);
    }
    currentLeaf = extractLeafNode(answer.answers, leafNodesArray);
  });

  return currentLeaf;
};


const sponsoredCategoryFilterArray = (facetFilters, filterData) => {
  let leafNodesIds = [];
  const response = [];

  const addSponsoredCategory = (facetFilterItem) => {
    const id = facetFilterItem.toString().split(':')[1];

    if (leafNodesIds.includes(id)) {
      response.push(`sponsoredCategory.id:${id}`);
    }
  };

  filterData.forEach((filterDataItem) => {
    leafNodesIds = extractLeafNode(filterDataItem.answers, leafNodesIds);
  });

  for (let i = 0; i < facetFilters.length; i++) {
    if (Array.isArray(facetFilters[i])) {
      // eslint-disable-next-line no-loop-func
      facetFilters[i].forEach(item => addSponsoredCategory(item));
    } else {
      addSponsoredCategory(facetFilters[i]);
    }
  }
  return uniq(response);
};

const getAlgoliaData = (algoliaConfig, indexName, request, filterData, exhibitorIdsToExclude = []) => {
  const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey, { protocol: 'https:' });
  const index = client.initIndex(indexName);
  let filters = request.filters ?? '';
  let exhibitorToExclude = '';
  if ((request.query || request.facetFilters?.length > 0) && (exhibitorIdsToExclude.length > 0)) {
    for (let i = 0; i < exhibitorIdsToExclude.length; i++) {
      exhibitorToExclude = exhibitorToExclude + (i===0 ? ` NOT objectID:${exhibitorIdsToExclude[i]}` : ` AND NOT objectID:${exhibitorIdsToExclude[i]}`);
    }
    filters = filters.length > 0 ? filters + ' AND'+ exhibitorToExclude : filters + exhibitorToExclude;
  }

  return new Promise(resolve => index.search('', {
    page: request.page,
    query: request.query,
    filters: filters,
    facetFilters: request.facetFilters,
    restrictSearchableAttributes: request.search,
    optionalFilters: sponsoredCategoryFilterArray(request.facetFilters, filterData)
  }, (err, result) => {
    if(err)
    Logger.logWarn('Error While fetching AlgoliaData',err.stack);
    resolve({ err, content: result });
  }));
};

const getPremiumExhibitorIdsFromAlgolia = async (algoliaConfig, indexName, request, filterData, packageId, res=[], page=0) => {
  const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey, { protocol: 'https:' });
  const index = client.initIndex(indexName);

  return new Promise(resolve => index.search('', {
    page: page,
    hitsPerPage: 1000,
    query: request.query,
    filters: request.filters,
    typoTolerance: false,
    numericFilters: [`packageId=${packageId}`],
    facetFilters: request.facetFilters,
    restrictSearchableAttributes: request.search,
    optionalFilters: sponsoredCategoryFilterArray(request.facetFilters, filterData),
    attributesToRetrieve: ['objectID', 'sponsoredCategory', 'video'],
    attributesToHighlight: []
  }, (err, result) => {
    if(err)
      Logger.logWarn('Error While fetching AlgoliaData',err.stack);
    resolve({ err, content: result });
  })).then( async (response) => {
    if (response.err)
      return { err: response.err, hits: res };
    res = res.concat(response.content.hits);
    if (response.content.page < response.content.nbPages-1 && response.content.nbPages <= 5) {
      return await getPremiumExhibitorIdsFromAlgolia(algoliaConfig, indexName, request, filterData, packageId, res, response.content.page+1);
    }
    else {
      return { err: response.err, hits: res };
    }
  });
};

const getAlgoliaDataById = (algoliaConfig, indexName, id) => {
  const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey, { protocol: 'https:' });
  const index = client.initIndex(indexName);
  return index.getObject(id);
};

const getAlgoliaDataByIds = async (algoliaConfig, indexName, exhibitorsIds, query) => {
  const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey, { protocol: 'https:' });
  const index = client.initIndex(indexName);
  let filters = '';
  for (let i = 0; i < exhibitorsIds.length; i++) {
    filters = filters + (i===0 ? `objectID:${exhibitorsIds[i]}` : ` OR objectID:${exhibitorsIds[i]}`);
  }

  return new Promise(resolve => index.search('', {
    query: query,
    filters: filters
  }, (err, result) => {
    if(err)
      Logger.logWarn('Error While fetching AlgoliaData',err.stack);
    resolve({ err, content: result });
  }));
};

export default {
  getFilterGroupNames, getAlgoliaData, getAlgoliaDataById, getFilterData, getAlgoliaDataByIds, getPremiumExhibitorIdsFromAlgolia, sponsoredCategoryFilterArray
};
